import { Capacitor } from "@capacitor/core";

const BIOMETRIC_ENABLED_PREFIX = "vaultquest:biometricLoginEnabled:";
const BIOMETRIC_OFFER_UID_KEY = "vaultquest:offerBiometricLoginForUid";

function isStorageAvailable() {
  return typeof window !== "undefined" && Boolean(window.localStorage);
}

function getUserKey(uid) {
  return `${BIOMETRIC_ENABLED_PREFIX}${uid}`;
}

export function isIosBiometricAuthSupportedPlatform() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios";
}

export function markBiometricOfferAfterLogin(uid) {
  if (!uid || typeof window === "undefined") return;

  try {
    window.sessionStorage?.setItem(BIOMETRIC_OFFER_UID_KEY, uid);
  } catch {
    // Non-critical convenience prompt.
  }
}

export function consumeBiometricOfferAfterLogin(uid) {
  if (!uid || typeof window === "undefined") return false;

  try {
    const storedUid = window.sessionStorage?.getItem(BIOMETRIC_OFFER_UID_KEY);
    if (storedUid !== uid) return false;
    window.sessionStorage?.removeItem(BIOMETRIC_OFFER_UID_KEY);
    return true;
  } catch {
    return false;
  }
}

export function isBiometricLoginEnabledForUser(uid) {
  if (!uid || !isStorageAvailable()) return false;

  try {
    return window.localStorage.getItem(getUserKey(uid)) === "1";
  } catch {
    return false;
  }
}

export function setBiometricLoginEnabledForUser(uid, enabled) {
  if (!uid || !isStorageAvailable()) return;

  try {
    if (enabled) {
      window.localStorage.setItem(getUserKey(uid), "1");
    } else {
      window.localStorage.removeItem(getUserKey(uid));
    }
  } catch {
    // The setting is non-sensitive and non-critical.
  }
}

export async function getBiometricAvailability() {
  if (!isIosBiometricAuthSupportedPlatform()) {
    return { isAvailable: false, reason: "not_ios_native" };
  }

  try {
    const { NativeBiometric } = await import("@capgo/capacitor-native-biometric");
    return NativeBiometric.isAvailable({ useFallback: false });
  } catch (error) {
    console.warn("Biometric availability check failed:", error?.code || error?.message || "unknown");
    return { isAvailable: false, reason: "availability_error" };
  }
}

export async function authenticateWithBiometrics() {
  if (!isIosBiometricAuthSupportedPlatform()) return false;

  try {
    const availability = await getBiometricAvailability();
    if (!availability?.isAvailable) return false;

    const { NativeBiometric } = await import("@capgo/capacitor-native-biometric");

    await NativeBiometric.verifyIdentity({
      title: "VaultQuest",
      subtitle: "Anmeldung entsperren",
      reason: "Entsperre VaultQuest mit Face ID oder Touch ID.",
      description: "Bestätige deine Identität, um deine bestehende Anmeldung zu verwenden.",
      negativeButtonText: "Abbrechen",
      fallbackTitle: "",
      useFallback: false,
    });

    return true;
  } catch (error) {
    console.warn("Biometric authentication failed or was cancelled:", error?.code || error?.message || "unknown");
    return false;
  }
}
